import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UNKNOWN_CELL } from '../utils/uiText';
import { FileText, ChevronRight, UploadCloud, Check, AlertTriangle, MessageSquarePlus, History, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useTab } from '../contexts/TabContext';
import { useZoeConversation } from '../contexts/ZoeConversationContext';
import { fetchInvestigatorHealth } from '../services/investigatorApi';
import { hasPageAccess } from '../utils/pageAccess';
import QueryBar, { PROFIT_HIGHLIGHTED_CHIP_WORDS_RE } from '../components/investigator/QueryBar';
import EmptyState from '../components/investigator/EmptyState';
import ZoeMark from '../components/zoeAssistant/ZoeMark';
import ZoeThread from '../components/zoeAssistant/ZoeThread';
import ZoeConversationList from '../components/zoeAssistant/ZoeConversationList';
import { ZOE_MORE_PROMPTS, ZOE_PRIMARY_PROMPTS } from '../components/zoeAssistant/zoePrompts';
import { useTypingPlaceholder } from '../hooks/useTypingPlaceholder';

const AGENTS_ZOE_INPUT_PLACEHOLDER = 'Ask ZOE about an order, vehicle, driver, or anything flagged today…';
/** Recent conversations shown on the empty page before "show all". */
const RECENT_PREVIEW = 5;

export default function Agents() {
  const { activeTab, setActiveTab } = useTab();

  type AgentStatus = 'ready' | 'degraded' | 'offline' | 'unknown';
  const [, setZoeStatus] = useState<AgentStatus>('unknown');
  const [customsStatus, setCustomsStatus] = useState<AgentStatus>('unknown');

  const [agentsSeenAt, setAgentsSeenAt] = useState<Date | null>(null);

  // I1-E6 / T30 — the conversation lives in ZoeConversationContext so the
  // global pop-up shares it: "Open full ZOE" continues the same thread, and a
  // conversation reopened here is the one the pop-up shows.
  const {
    turns,
    isLoading,
    entitySuggestions,
    chipIntroOnboarded: zoeChipIntroOnboarded,
    ask,
    newConversation,
    conversationId,
    conversationTitle,
    recentConversations,
    refreshRecent,
    resumeConversation,
    removeConversation,
    resumingId,
    resumeError,
    ensureResumed,
  } = useZoeConversation();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [draftQuestion, setDraftQuestion] = useState<string>('');
  const typedPlaceholder = useTypingPlaceholder(AGENTS_ZOE_INPUT_PLACEHOLDER);
  const latestTurnRef = useRef<HTMLDivElement | null>(null);
  const cardsRootRef = useRef<HTMLDivElement | null>(null);

  const latestTurn = turns[turns.length - 1];

  /** Bring the newest answer into view inside App’s overflow scroll pane (not window). */
  const scrollToLatestTurn = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        latestTurnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, []);

  // Scroll to a turn when it settles (answer, question back or error), or to
  // the newest turn of a conversation just reopened. A turn that settled in
  // the pop-up is scrolled to when this page next shows ("Open full ZOE"),
  // not while the page is hidden.
  const settledKey = latestTurn && latestTurn.status !== 'loading' ? latestTurn.id : null;
  const lastScrolledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeTab !== 'agents') return;
    if (!settledKey || settledKey === lastScrolledKeyRef.current) return;
    lastScrolledKeyRef.current = settledKey;
    scrollToLatestTurn();
  }, [settledKey, activeTab, scrollToLatestTurn]);

  const lastUsed = useMemo(() => {
    const readIso = (k: string) => {
      try {
        const v = localStorage.getItem(k);
        return v ? new Date(v) : null;
      } catch {
        return null;
      }
    };
    return {
      customs: readIso('agents_last_used_customs'),
      profit: readIso('agents_last_used_profit'),
    };
  }, []);

  const lastResult = useMemo(() => {
    const readIso = (k: string) => {
      try {
        const v = localStorage.getItem(k);
        return v ? new Date(v) : null;
      } catch {
        return null;
      }
    };
    const readNum = (k: string) => {
      try {
        const v = localStorage.getItem(k);
        return v ? Number(v) : 0;
      } catch {
        return 0;
      }
    };
    return {
      customs: {
        ts: readIso('agents_last_result_customs_ts'),
        items: readNum('agents_last_result_customs_items'),
        converted: readNum('agents_last_result_customs_converted'),
        attention: readNum('agents_last_result_customs_attention'),
      },
    };
  }, []);

  const fmtLastUsed = (d: Date | null) => {
    if (!d) return `Last used: ${UNKNOWN_CELL}`;
    const diffMs = Date.now() - d.getTime();
    const mins = Math.max(0, Math.round(diffMs / 60000));
    if (mins < 1) return 'Last used: just now';
    if (mins < 60) return `Last used: ${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 48) return `Last used: ${hours}h ago`;
    return `Last used: ${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
  };

  useEffect(() => {
    try {
      const prev = localStorage.getItem('agents_last_seen');
      setAgentsSeenAt(prev ? new Date(prev) : null);
      localStorage.setItem('agents_last_seen', new Date().toISOString());
    } catch {
      setAgentsSeenAt(null);
    }

    let cancelled = false;

    const check = async () => {
      // ZOE readiness: investigator health endpoint (auth required)
      try {
        const h = await fetchInvestigatorHealth();
        if (!cancelled) setZoeStatus(h.status === 'ok' && h.llm_available && h.truth_layer_available ? 'ready' : 'degraded');
      } catch {
        // If the server isn't reachable (e.g. local backend not running), treat as offline.
        if (!cancelled) setZoeStatus('offline');
      }

      // Customs readiness: python pdf extractor health (best-effort)
      try {
        const res = await fetch('/api/customs/extract-pdf-python/health');
        if (!cancelled) setCustomsStatus(res.ok ? 'ready' : 'degraded');
      } catch {
        if (!cancelled) setCustomsStatus('offline');
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasNewSinceSeen = (ts: Date | null) => {
    if (!ts) return false;
    const seen = agentsSeenAt;
    if (!seen) return false;
    return ts.getTime() > seen.getTime();
  };

  const statusDot = (s: AgentStatus) => {
    const cls =
      s === 'ready'
        ? 'bg-success'
        : s === 'degraded'
          ? 'bg-warning'
          : s === 'offline'
            ? 'bg-destructive'
          : 'bg-accent';
    const label = s === 'ready' ? 'Ready' : s === 'degraded' ? 'Partial' : s === 'offline' ? 'Offline' : 'Checking…';
    return (
      <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
        <span className={`w-2.5 h-2.5 rounded-full ${cls}`} />
        <span>{label}</span>
      </span>
    );
  };

  const badge = (text: string) => (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-background/40 border border-border/60 text-xs text-foreground">
      {text}
    </span>
  );

  const kpiPillGreen = (n: number) => (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/25 border border-success/40 text-xs text-success whitespace-nowrap">
      <Check className="w-4 h-4" />
      <span className="font-semibold">{n}</span>
    </span>
  );

  const kpiPillRed = (n: number) => (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-destructive/25 border border-destructive/40 text-xs text-destructive whitespace-nowrap">
      <AlertTriangle className="w-4 h-4" />
      <span className="font-semibold">{n}</span>
    </span>
  );

  const handleSubmit = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || isLoading) return;
      // Cleared on send, as in any chat: the question now lives in the thread
      // as the user's message, so leaving it in the box would show it twice.
      setDraftQuestion('');
      void ask(trimmed);
    },
    [ask, isLoading],
  );

  /** I1-E6 — "New conversation": drop the thread and its server-side id. The
   *  conversation itself stays in History. */
  const handleNewConversation = useCallback(() => {
    newConversation();
    setHistoryOpen(false);
    setDraftQuestion('');
  }, [newConversation]);

  /** T30 — reopen a conversation from History or the Recent list. */
  const openConversation = useCallback(
    async (id: string) => {
      if (await resumeConversation(id)) {
        setHistoryOpen(false);
        setDraftQuestion('');
      }
    },
    [resumeConversation],
  );

  // T30 — on first showing: list recent conversations, and reopen the one this
  // tab was in before a reload (unless the pop-up already has, or a question
  // is on its way — see ensureResumed).
  useEffect(() => {
    void refreshRecent();
    ensureResumed();
  }, [refreshRecent, ensureResumed]);

  /**
   * The one input on this page. Rendered at the top on an empty page, and at
   * the end of the thread once a conversation has started — never both.
   * Controlled (`draftQuestion`), so moving it between the two positions when
   * the first turn lands loses nothing the user has typed.
   */
  const renderComposer = ({ showSuggestedPrompts }: { showSuggestedPrompts: boolean }) => (
    <QueryBar
      onSubmit={handleSubmit}
      isLoading={isLoading}
      zoeChipIntroOnboarded={zoeChipIntroOnboarded}
      placeholder={turns.length > 0 ? 'Ask a follow-up…' : typedPlaceholder}
      suggestions={entitySuggestions}
      value={draftQuestion}
      onValueChange={setDraftQuestion}
      primarySuggestedPrompts={ZOE_PRIMARY_PROMPTS}
      moreSuggestedPrompts={ZOE_MORE_PROMPTS}
      highlightPattern={PROFIT_HIGHLIGHTED_CHIP_WORDS_RE}
      submitLabel={turns.length > 0 ? 'Send' : 'Ask ZOE'}
      loadingLabel="ZOE is thinking…"
      showSuggestedPrompts={showSuggestedPrompts}
    />
  );

  const canOpenCustomsAgent = hasPageAccess('customs-agent');

  const runCustomsQuickAction = () => {
    if (!canOpenCustomsAgent) return;
    try {
      localStorage.setItem('agents_quick_action_customs_upload', '1');
    } catch {
      // ignore
    }
    setActiveTab('customs-agent');
  };

  // Support legacy quick-action key if something else sets it.
  useEffect(() => {
    try {
      const q = localStorage.getItem('agents_quick_action_ops_assistant_question');
      if (!q) return;
      localStorage.removeItem('agents_quick_action_ops_assistant_question');
      handleSubmit(q);
    } catch {
      // ignore
    }
  }, [handleSubmit]);

  // Fade-in cards on scroll into view (IntersectionObserver).
  useEffect(() => {
    const root = cardsRootRef.current;
    if (!root) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const cards = Array.from(root.querySelectorAll<HTMLElement>('.zoe-agent-card'));
    if (cards.length === 0) return;

    if (prefersReduced) {
      for (const el of cards) el.classList.add('zoe-inview');
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      for (const el of cards) el.classList.add('zoe-inview');
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          (e.target as HTMLElement).classList.add('zoe-inview');
          obs.unobserve(e.target);
        }
      },
      { threshold: 0.15 },
    );

    for (const el of cards) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="space-y-6 w-full zoe-fluid-scale">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-zoe-900/30 border border-zoe-500/40">
          <ZoeMark className="w-5 h-5 text-zoe-300" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">ZOE · Operational Agent</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Ask about today’s risks, delays and fleet issues, and get a clear next step.
          </p>
        </div>
      </div>

      {/* ZOE Q&A — a chat thread.
          Empty state: the prompt sits at the top with conversation starters, as
          the page's landing view. Once a conversation starts, the SAME composer
          moves to the end of the thread, so there is only ever one input and it
          is always where the conversation ends — the familiar chat pattern. */}
      <div className="space-y-3">
        {turns.length === 0 ? (
          <>
            {renderComposer({ showSuggestedPrompts: true })}

            {resumeError ? (
              <p className="rounded-lg border border-warning/40 bg-warning/20 px-3 py-2 text-xs text-warning" role="status">
                {resumeError}
              </p>
            ) : null}

            {/* T30 — pick a conversation up where it was left. */}
            {recentConversations.length > 0 ? (
              <section aria-labelledby="zoe-recent-heading" className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <h2 id="zoe-recent-heading" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Recent conversations
                  </h2>
                  {resumingId ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      Opening…
                    </span>
                  ) : null}
                </div>
                <div className="rounded-xl border border-border bg-card/30 p-1">
                  <ZoeConversationList
                    conversations={historyOpen ? recentConversations : recentConversations.slice(0, RECENT_PREVIEW)}
                    onOpen={(id) => void openConversation(id)}
                    onDelete={removeConversation}
                    disabled={Boolean(resumingId)}
                  />
                  {recentConversations.length > RECENT_PREVIEW ? (
                    <button
                      type="button"
                      onClick={() => setHistoryOpen((open) => !open)}
                      aria-expanded={historyOpen}
                      className="flex w-full items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-xs text-muted-foreground hover:bg-card/60 hover:text-foreground"
                    >
                      {historyOpen ? (
                        <>Show fewer <ChevronUp className="h-3.5 w-3.5" aria-hidden /></>
                      ) : (
                        <>Show all {recentConversations.length} <ChevronDown className="h-3.5 w-3.5" aria-hidden /></>
                      )}
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}

            <EmptyState />
          </>
        ) : (
          <>
            {/* T30 — the open conversation: its name, its history, a way out. */}
            <div className="flex items-center gap-2">
              <h2
                className="min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground"
                title={conversationTitle || undefined}
              >
                {conversationTitle || 'New conversation'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setHistoryOpen((open) => !open);
                  if (!historyOpen) void refreshRecent();
                }}
                aria-expanded={historyOpen}
                aria-controls="zoe-history-panel"
                className="inline-flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
              >
                <History className="w-3.5 h-3.5" aria-hidden />
                History
              </button>
              <button
                type="button"
                onClick={handleNewConversation}
                disabled={isLoading}
                className="inline-flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:border-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MessageSquarePlus className="w-3.5 h-3.5" aria-hidden />
                New conversation
              </button>
            </div>

            {historyOpen ? (
              <div id="zoe-history-panel" className="rounded-xl border border-border bg-card/30 p-1">
                {recentConversations.length > 0 ? (
                  <ZoeConversationList
                    conversations={recentConversations}
                    activeId={conversationId}
                    onOpen={(id) => {
                      if (id === conversationId) setHistoryOpen(false);
                      else void openConversation(id);
                    }}
                    onDelete={removeConversation}
                    disabled={Boolean(resumingId) || isLoading}
                  />
                ) : (
                  <p className="px-3 py-2 text-xs text-muted-foreground">No other conversations yet.</p>
                )}
              </div>
            ) : null}

            {/* The thread, shared with the pop-up: every turn is the user's
                message followed by ZOE's response to it. Rendered only while
                this page shows: it stays mounted when hidden, and a hidden copy
                would type out every pop-up answer a second time. */}
            {activeTab === 'agents' ? <ZoeThread latestTurnRef={latestTurnRef} /> : null}

            {/* Follow-up composer: where the conversation ends, as in any chat. */}
            <div className="pt-2">
              {renderComposer({ showSuggestedPrompts: false })}
            </div>
          </>
        )}
      </div>

      <div className="space-y-3 mt-10" ref={cardsRootRef}>
        {/* Custom styled title with line dividers (same pattern as Daily Profitability Trend on Profit page) */}
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center" aria-hidden>
            <div className="w-full border-t border-accent" />
          </div>
          <div className="relative flex flex-col items-center">
            <div className="bg-background px-4">
              <h2 className="text-xl font-semibold text-foreground tracking-tight text-center">Your agents</h2>
            </div>
          </div>
        </div>

        <div className="zoe-agent-grid grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="zoe-agent-card w-full flex flex-col gap-2 group rounded-xl border border-indigo-500/55 bg-card/30 p-3 shadow-card-glow-indigo transition-all duration-500 hover:-translate-y-1.5 hover:border-indigo-200/70 hover:bg-card/45 hover:shadow-card-glow-indigo-hover">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-indigo-900/25 border border-indigo-500/40">
                <FileText className="w-6 h-6 text-indigo-200 transition-transform duration-500 group-hover:scale-135" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {canOpenCustomsAgent ? (
                    <button
                      type="button"
                      className="text-base font-semibold text-foreground transition-colors group-hover:text-indigo-50 hover:underline"
                      onClick={() => setActiveTab('customs-agent')}
                    >
                      Document Agent
                    </button>
                  ) : (
                    <span className="text-base font-semibold text-foreground cursor-default">
                      Document Agent
                    </span>
                  )}
                  {hasNewSinceSeen(lastResult.customs.ts) ? (
                    <>
                      {lastResult.customs.converted > 0 ? kpiPillGreen(lastResult.customs.converted) : null}
                      {lastResult.customs.attention > 0 ? kpiPillRed(lastResult.customs.attention) : null}
                      {lastResult.customs.converted === 0 && lastResult.customs.attention === 0 && lastResult.customs.items > 0
                        ? badge(`${lastResult.customs.items} items processed`)
                        : null}
                      {lastResult.customs.items === 0 ? badge('New result') : null}
                    </>
                  ) : null}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {statusDot(customsStatus)}
                  <span className="text-sm text-muted-foreground">{fmtLastUsed(lastUsed.customs)}</span>
                </div>
              </div>
            </div>
            {canOpenCustomsAgent ? (
              <button
                type="button"
                onClick={() => setActiveTab('customs-agent')}
                className="p-2 rounded-lg text-muted-foreground hover:text-indigo-200 hover:bg-indigo-950/20 transition-colors"
                aria-label="Open Document Agent"
                title="Open"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            ) : (
              <span className="p-2 rounded-lg text-muted-foreground cursor-default" aria-hidden>
                <ChevronRight className="w-6 h-6" />
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canOpenCustomsAgent ? (
              <button
                type="button"
                onClick={runCustomsQuickAction}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-900/45 border border-indigo-500/55 text-base text-foreground transition-all duration-150 hover:bg-indigo-500/55 hover:border-indigo-400/70 hover:text-white hover:shadow-glow-indigo-xl active:scale-98 active:bg-indigo-600/60"
              >
                <UploadCloud className="w-5 h-5" />
                Upload invoice
              </button>
            ) : (
              <span
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-900/25 border border-indigo-500/30 text-base text-muted-foreground cursor-default"
                title="Document Agent access required"
              >
                <UploadCloud className="w-5 h-5" />
                Upload invoice
              </span>
            )}
          </div>
        </div>

        </div>
      </div>
    </div>
  );
}

